<template>
  <div
      v-if="this.$store.state.auth.me.permissions.includes('sunset_note_stats_restricted') || this.$store.state.auth.me.permissions.includes('sunset_note_stats_leaderboard')"
      style="width: 100%; height: 100%;">
    <section v-if="!isLoading && isNoteStatsRoute">
      <h2>Query controls</h2>

      <!-- Controls grid -->
      <div style="display:grid;grid-template-columns:200px auto;gap:.75rem 1rem; align-items:center">
        <!-- Date range -->
        <label>Date range</label>
        <div style="display:grid;grid-auto-flow:column; gap:.75rem 1rem;align-items:center">
          <b-button class="dr-button" type="is-text" @click="resetToToday">Today</b-button>
          <b-button class="dr-button" type="is-text" @click="resetToThisWeek">This Week</b-button>
          <b-button class="dr-button" type="is-text" @click="resetToThisMonth">This Month</b-button>
          <b-button class="dr-button" type="is-text" @click="resetToThisQuarter">This Quarter</b-button>
          <b-button class="dr-button" type="is-text" @click="resetToLastMonth">Last Month</b-button>
          <b-button class="dr-button" type="is-text" @click="resetToLastQuarter">Last Quarter</b-button>
          <b-button class="dr-button" type="is-text" @click="resetToLast6Months">Last 6 Months</b-button>
          <b-button class="dr-button" type="is-text" @click="resetToLastYear">Last Year</b-button>

          <div style="display:grid;grid-template-columns:1fr 1fr; grid-column-gap: 0.75rem">
            <b-datepicker
                v-model="startDate"
                :date-formatter="formatPicker"
                :date-parser="parsePicker"
                :editable="false"
                :max-date="endDate"
                placeholder="Select start"
                style="align-self: center; justify-self: center; min-width: 250px; width: 100%;"
            />
            <b-datepicker
                v-model="endDate"
                :date-formatter="formatPicker"
                :date-parser="parsePicker"
                :editable="false"
                :min-date="startDate"
                placeholder="Select end"
                style="align-self: center; justify-self: center; min-width: 250px; width: 100%;"
            />
          </div>
        </div>

        <!-- Type (side) -->
        <label>Type</label>
        <b-select v-model="state.side" expanded>
          <option value="combined">Sales and Service</option>
          <option value="sales">Sales</option>
          <option value="service">Service</option>
        </b-select>

        <template
            v-if="$store.state.auth.me.permissions.includes('sunset_note_stats_restricted')"
        >
          <!-- Mask -->
          <label>Quick filter note type</label>
          <b-select v-model="state.mask" expanded>
            <option v-for="m in maskOptions" :key="m.key" :value="m.key">
              {{ m.label }}
            </option>
          </b-select>

          <!-- Match / Not match (apply on blur/Enter) -->
          <label>Filter note type</label>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:.75rem 1rem;align-items:center">
            <b-input
                v-model.trim="inputs.match"
                placeholder="Match regexp (e.g. hot|email)"
                @blur="commitFilters"
                @keyup.enter="commitFilters"
            />
            <b-input
                v-model.trim="inputs.notMatch"
                placeholder="Not match regexp (e.g. hot|email)"
                @blur="commitFilters"
                @keyup.enter="commitFilters"
                disabled="disabled"
            />
          </div>

          <!-- Windowed -->
          <label>By window<br />(e.g. AM, PM or OT)</label>
          <b-checkbox v-model="state.windowed" />
        </template>

        <!-- User - only on user routes -->
        <template v-if="isUserRoute">
          <label>User</label>
          <b-select v-model="state.userId" expanded placeholder="Select a user">
            <option v-for="u in userOptions" :key="u.id" :value="u.id">
              {{ u.user_name }}
            </option>
          </b-select>
        </template>

        <!-- Limit - for userNotes and globalNotes -->
        <template v-if="routeType === 'userNotes' || routeType === 'globalNotes'">
          <label>Limit (top N)</label>
          <div style="display:grid;grid-auto-flow:column;gap:.5rem;align-items:center">
            <b-input
                v-model.number="state.limit"
                type="number"
                min="1"
                step="1"
                placeholder="All"
                style="max-width: 140px"
                disabled="disabled"
            />
            <!--<b-button type="is-text" @click="state.limit = null">All</b-button>-->
            <b-button type="is-text" @click="state.limit = 300">300</b-button>
            <b-button type="is-text" @click="state.limit = 100">100</b-button>
            <b-button type="is-text" @click="state.limit = 50">50</b-button>
            <b-button type="is-text" @click="state.limit = 25">25</b-button>
            <b-button type="is-text" @click="state.limit = 10">10</b-button>
          </div>
        </template>
      </div>

      <!-- Empty state helper -->
      <article v-if="!hasRequiredParams" class="message is-info mt-4">
        <div class="message-body">
          <strong>Choose a date range{{ isUserRoute ? ' and a user' : '' }}</strong> to load results.
        </div>
      </article>
    </section>

    <!-- Expose params to consumer slot (only when ready) -->
    <slot
        v-if="!isLoading && isNoteStatsRoute && hasRequiredParams"
        :currentUser="currentUser"
        :meta="meta"
        :params="exposedParams"
        :path="applyPath"
        :query="normalizedQuery"
        :queryString="queryString"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { DateTime } from 'luxon';
import { formatDate } from './utils';

export default {
  name: 'sunset_note_stats_criteria_bar',

  props: {
    side: { type: String, default: 'combined' },
    mask: { type: String, default: 'all' },
    match: { type: String, default: '' },
    notMatch: { type: String, default: '' },
    includeOther: { type: [Boolean, String, Number], default: true },
    windowed: { type: [Boolean, String, Number], default: false },
    start: { type: String, default: '' },
    end:   { type: String, default: '' },
    userId: { type: [Number, String], default: null },
    datePathFormat: { type: String, default: 'dd-LL-yyyy' },

    // If true, force a full page reload when updating the URL
    hardReloadOnChange: { type: Boolean, default: false },

    // Default limit for note lists when no ?limit= is present (used for userNotes & globalNotes)
    defaultUserNotesLimit: { type: [Number, String], default: 300 },
  },

  data() {
    return {
      apiBaseUrl: '/api/v1/',
      isLoading: true,
      meta: {},
      maskOptions: [],
      state: {
        side: 'combined',
        mask: 'all',
        match: '',
        notMatch: '',
        includeOther: true,
        windowed: false,
        userId: null,
        startAt: null,
        endAt: null,
        limit: null, // userNotes & globalNotes
      },
      // Inputs for match/notMatch that only commit on blur/Enter
      inputs: {
        match: '',
        notMatch: '',
      },
      _navTimer: null,
      _metaTimer: null,
      _filtersTimer: null,
    };
  },

  async mounted() {
    if (this.isNoteStatsRoute) {
      this.hydrateFromRoute();
      // fetch meta when dates are ready (even if userId is not selected yet)
      if (this.hasDateParams) await this.fetchMeta();
    }
    this.isLoading = false;
  },

  beforeDestroy() {
    this.clearTimers();
  },

  watch: {
    $route() {
      if (this.isNoteStatsRoute) {
        this.hydrateFromRoute();
        // refresh meta when dates change
        if (this.hasDateParams) this.scheduleMetaRefresh();
      } else {
        this.clearTimers();
      }
    },

    // URL rewrite when inputs → route change (only when valid)
    applyPath()   { if (this.isNoteStatsRoute) this.scheduleUrlUpdate(); },
    queryString() { if (this.isNoteStatsRoute) this.scheduleUrlUpdate(); },

    // Refresh meta when key inputs change (debounced)
    'state.side':    'scheduleMetaRefresh',
    'state.windowed':'scheduleMetaRefresh',
    'state.startAt': 'scheduleMetaRefresh',
    'state.endAt':   'scheduleMetaRefresh',
  },

  computed: {
    apiBase() {
      return this.apiBaseUrl.replace(/\/+$/, '');
    },

    isNoteStatsRoute() {
      const p = this.$route?.path || (typeof location !== 'undefined' ? location.pathname : '');
      return /\/note-stats\//.test(p);
    },

    routeType() {
      const p = this.$route?.path || (typeof location !== 'undefined' ? location.pathname : '');
      if (/\/note-stats\/leaderboard\//.test(p)) return 'leaderboard';
      if (/\/note-stats\/notes\/user\//.test(p)) return 'userNotes';
      if (/\/note-stats\/notes\//.test(p)) return 'globalNotes';
      if (/\/note-stats\/user(\/|$)/.test(p)) return 'userTotals';
      return 'totals';
    },

    isUserRoute() {
      return this.routeType === 'userNotes' || this.routeType === 'userTotals';
    },

    // NEW: dates-only guard so meta can load users after dates are chosen
    hasDateParams() {
      return !!(this.state.startAt && this.state.endAt);
    },

    // Initial-mode gate for rendering results
    hasRequiredParams() {
      const hasDates = !!(this.state.startAt && this.state.endAt);
      const hasUserIfNeeded = !this.isUserRoute || this.state.userId != null;
      return hasDates && hasUserIfNeeded;
    },

    routePrefix() {
      const p = this.$route?.path || (typeof location !== 'undefined' ? location.pathname : '');
      const i = p.indexOf('/note-stats/');
      return i > -1 ? p.slice(0, i) : '';
    },

    // Date adapters for <b-datepicker>
    startDate: {
      get() { return this.state.startAt ? this.state.startAt.toJSDate() : null; },
      set(d) { if (d instanceof Date && !isNaN(d)) this.state.startAt = DateTime.fromJSDate(d).startOf('day'); },
    },
    endDate: {
      get() { return this.state.endAt ? this.state.endAt.toJSDate() : null; },
      set(d) { if (d instanceof Date && !isNaN(d)) this.state.endAt = DateTime.fromJSDate(d).endOf('day'); },
    },

    // Users
    usersRaw() {
      const m = this.meta || {};
      if (Array.isArray(m.user)) return m.user;
      if (Array.isArray(m.users)) return m.users;
      return [];
    },
    userOptions() {
      return this.usersRaw
          .filter(u => u && (u.id != null || u.user_id != null || u.userid != null))
          .map(u => {
            const id = String(u.id != null ? u.id : (u.user_id != null ? u.user_id : u.userid));
            const label = (u.user_name || u.name || (`${u.first_name || ''} ${u.surname || ''}`).trim() || id);
            return { id, user_name: String(label) };
          });
    },
    currentUser() {
      const id = this.state.userId;
      if (id == null) return null;
      const sid = String(id);
      for (const x of this.usersRaw) {
        const xid = String(x.id != null ? x.id : (x.user_id != null ? x.user_id : x.userid));
        if (xid === sid) return x;
      }
      return null;
    },

    // Routing bits
    canApply() {
      return this.hasRequiredParams;
    },

    applyPath() {
      // No fallback; only build when dates (and user for user routes) exist.
      const side = this.state.side || 'combined';
      const startAt = this.state.startAt;
      const endAt = this.state.endAt;

      if (!startAt || !endAt) return '';

      const s = this.formatDate(startAt, this.datePathFormat, '');
      const e = this.formatDate(endAt, this.datePathFormat, '');

      if (this.routeType === 'userNotes') {
        const uid = this.state.userId != null ? String(this.state.userId) : '';
        if (!uid) return '';
        return `${this.routePrefix}/note-stats/notes/user/${encodeURIComponent(uid)}/${encodeURIComponent(side)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`;
      }
      if (this.routeType === 'globalNotes') {
        return `${this.routePrefix}/note-stats/notes/${encodeURIComponent(side)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`;
      }
      if (this.routeType === 'userTotals') {
        const uid = this.state.userId != null ? String(this.state.userId) : '';
        if (!uid) return '';
        return `${this.routePrefix}/note-stats/user/${encodeURIComponent(uid)}/${encodeURIComponent(side)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`;
      }
      if (this.routeType === 'leaderboard') {
        return `${this.routePrefix}/note-stats/leaderboard/${encodeURIComponent(side)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`;
      }
      return `${this.routePrefix}/note-stats/totals/${encodeURIComponent(side)}/${encodeURIComponent(s)}/${encodeURIComponent(e)}`;
    },

    normalizedLimit() {
      const v = this.state.limit;
      if (v === null || v === '' || typeof v === 'undefined') return null;
      const n = parseInt(v, 10);
      if (!Number.isFinite(n) || n < 1) return null;
      return Math.min(1000, n);
    },

    normalizedQuery() {
      const q = {};
      if (this.state.mask) q.mask = this.state.mask;
      if (this.state.match) q.match = this.state.match;
      if (this.state.notMatch) q.not = this.state.notMatch;
      q.includeOther = this.state.includeOther ? '1' : '0';
      if (this.state.windowed) q.windowed = '1';

      if (!this.isUserRoute && this.state.userId != null && !Number.isNaN(this.state.userId)) {
        q.user = String(this.state.userId);
      }

      // Enable paging + limit for userNotes AND globalNotes
      if (this.routeType === 'userNotes' || this.routeType === 'globalNotes') {
        q.page = '1';
        const curQuery = (this.$route && this.$route.query) ? this.$route.query : {};
        if (curQuery && curQuery.perPage != null && curQuery.perPage !== '') {
          q.perPage = String(curQuery.perPage);
        }
        const n = this.normalizedLimit;
        if (n != null) q.limit = String(n);
      }

      return q;
    },

    queryString() {
      const usp = new URLSearchParams();
      const q = this.normalizedQuery;
      Object.keys(q).forEach(k => {
        const v = q[k];
        if (v !== undefined && v !== null) usp.set(k, String(v));
      });
      return usp.toString();
    },

    applyRoute() {
      return { path: this.applyPath, query: this.normalizedQuery };
    },

    exposedParams() {
      return {
        side: this.state.side,
        mask: this.state.mask,
        match: this.state.match || null,
        notMatch: this.state.notMatch || null,
        includeOther: !!this.state.includeOther,
        windowed: !!this.state.windowed,
        start: this.state.startAt ? this.formatDate(this.state.startAt, 'yyyy-LL-dd HH:mm:ss', '') : null,
        end: this.state.endAt ? this.formatDate(this.state.endAt, 'yyyy-LL-dd HH:mm:ss', '') : null,
        userId: this.state.userId != null ? this.state.userId : null,
        // Expose limit on userNotes & globalNotes
        limit: (this.routeType === 'userNotes' || this.routeType === 'globalNotes') ? this.normalizedLimit : null,
        apiBaseUrl: this.apiBase,
      };
    },
  },

  methods: {
    // --- housekeeping --------------------------------------------------------
    clearTimers() {
      if (this._navTimer)     { clearTimeout(this._navTimer); this._navTimer = null; }
      if (this._metaTimer)    { clearTimeout(this._metaTimer); this._metaTimer = null; }
      if (this._filtersTimer) { clearTimeout(this._filtersTimer); this._filtersTimer = null; }
    },

    // --- filter (match/not) application (on blur/Enter) ---------------------
    commitFilters() {
      const changed =
          this.state.match !== this.inputs.match ||
          this.state.notMatch !== this.inputs.notMatch;

      if (!changed) return;

      this.state.match = this.inputs.match;
      this.state.notMatch = this.inputs.notMatch;

      // URL update will be triggered by queryString watcher;
      // call explicitly to be snappy.
      this.scheduleUrlUpdate();
    },

    // --- navigation/url updates ---------------------------------------------
    scheduleUrlUpdate() {
      if (!this.isNoteStatsRoute || !this.canApply || !this.applyPath) return;
      if (this._navTimer) clearTimeout(this._navTimer);
      this._navTimer = setTimeout(() => this.navigateToState(), 0);
    },

    async navigateToState() {
      if (!this.isNoteStatsRoute || !this.canApply || !this.applyPath) return;

      const target = this.applyRoute; // { path, query }
      const samePath  = this.$route && this.$route.path === target.path;
      const sameQuery = this.$route && JSON.stringify(this.$route.query || {}) === JSON.stringify(target.query || {});
      if (samePath && sameQuery) return;

      const qs = this.queryString ? `?${this.queryString}` : '';
      const url = `${this.applyPath}${qs}`;

      if (this.hardReloadOnChange || !this.$router || typeof this.$router.replace !== 'function') {
        window.location.replace(url);
      } else {
        try {
          await this.$router.replace(target);
        } catch (_) { /* ignore NavigationDuplicated */ }
      }
    },

    // --- meta refresh --------------------------------------------------------
    scheduleMetaRefresh() {
      if (!this.isNoteStatsRoute || !this.hasDateParams) return;
      if (this._metaTimer) clearTimeout(this._metaTimer);
      this._metaTimer = setTimeout(() => { this.fetchMeta().catch(() => {}); }, 200);
    },

    async fetchMeta() {
      if (!this.hasDateParams) return;

      const s = this.formatDate(this.state.startAt, 'yyyy-LL-dd HH:mm:ss');
      const e = this.formatDate(this.state.endAt, 'yyyy-LL-dd HH:mm:ss');

      const usp = new URLSearchParams({
        side: this.state.side || 'combined',
        mask: 'all',
        start: s,
        end: e,
        windowed: this.state.windowed ? '1' : '0',
      });

      const url = `${this.apiBase}/sunset/reports/note-stats/meta?${usp.toString()}`;

      try {
        const { data } = await axios.get(url);
        this.meta = data || {};
        const masks = Array.isArray(this.meta.masks) ? this.meta.masks : [];
        this.maskOptions = masks.map(m => ({ key: String(m.key), label: String(m.label || m.key) }));
      } catch {
        this.meta = {};
        this.maskOptions = [];
      }
    },

    // --- helpers -------------------------------------------------------------
    // Parse & clamp limit
    coerceLimit(v) {
      const n = Number(v);
      if (!Number.isFinite(n) || n < 1) return null;
      return Math.min(1000, Math.floor(n));
    },

    // Route → state
    hydrateFromRoute() {
      const p = this.$route?.path || (typeof location !== 'undefined' ? location.pathname : '');
      const params = this.$route?.params || {};
      const query = this.$route?.query || {};

      const mLeaderboard = p.match(/^(.*)\/note-stats\/leaderboard\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);
      const mUserNotes   = p.match(/^(.*)\/note-stats\/notes\/user\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);
      const mGlobalNotes = p.match(/^(.*)\/note-stats\/notes\/([^/]+)\/([^/]+)\/([^/]+)\/?$/); // NEW
      const mUser        = p.match(/^(.*)\/note-stats\/user\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);
      const mTot         = p.match(/^(.*)\/note-stats\/totals\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);

      let side  = (params.type || query.side || this.side || 'combined');
      let start = (params.start || query.start || this.start || '');
      let end   = (params.end || query.end || this.end || '');

      if (mLeaderboard) {
        side  = mLeaderboard[2];
        start = mLeaderboard[3];
        end   = mLeaderboard[4];
        this.state.userId = null;
      } else if (mUserNotes) {
        side  = mUserNotes[3];
        start = mUserNotes[4];
        end   = mUserNotes[5];
        const routeUid = params.userid || mUserNotes[2] || query.user || query.userId || query.user_id || this.userId;
        this.state.userId = this.parseUserId(routeUid);
      } else if (mGlobalNotes) { // NEW
        side  = mGlobalNotes[2];
        start = mGlobalNotes[3];
        end   = mGlobalNotes[4];
        this.state.userId = null; // ensure cleared
      } else if (mUser) {
        side  = mUser[3];
        start = mUser[4];
        end   = mUser[5];
        const routeUid = params.userid || mUser[2] || query.user || query.userId || query.user_id || this.userId;
        this.state.userId = this.parseUserId(routeUid);
      } else if (mTot) {
        side  = mTot[2];
        start = mTot[3];
        end   = mTot[4];
        const qUser = query.user || query.userId || query.user_id;
        this.state.userId = this.parseUserId(qUser != null ? qUser : this.userId);
      }

      this.state.side = ['sales', 'service', 'combined'].includes(String(side).toLowerCase())
          ? String(side).toLowerCase()
          : 'combined';

      // Leave null when missing/invalid (initial mode)
      const sa = this.parseDateToBoundary(start, true);
      const ea = this.parseDateToBoundary(end, false);
      this.state.startAt = sa || null;
      this.state.endAt   = ea || null;

      // Filters
      this.state.mask         = (query.mask !== undefined) ? String(query.mask) : (this.mask || 'all');
      this.state.match        = (query.match !== undefined && query.match !== null) ? String(query.match) : (this.match || '');
      this.state.notMatch     = (query.not   !== undefined && query.not   !== null) ? String(query.not)   : (this.notMatch || '');
      this.state.includeOther = this.parseBool((query.includeOther !== undefined ? query.includeOther : this.includeOther), true);
      this.state.windowed     = this.parseBool((query.windowed     !== undefined ? query.windowed     : this.windowed), false);

      // Keep inputs in sync (so text boxes reflect current state without navigating while typing)
      this.inputs.match    = this.state.match;
      this.inputs.notMatch = this.state.notMatch;

      // Limit handling (userNotes & globalNotes)
      if (this.routeType === 'userNotes' || this.routeType === 'globalNotes') {
        if (query.limit !== undefined && query.limit !== '') {
          this.state.limit = this.coerceLimit(query.limit);
        } else {
          this.state.limit = this.coerceLimit(this.defaultUserNotesLimit);
        }
      } else {
        this.state.limit = null;
      }
    },

    // Utils passed to template
    formatDate,
    parseUserId(maybeId) {
      if (maybeId === '' || maybeId === 'null' || maybeId == null) return null;
      const n = Number(maybeId);
      return Number.isFinite(n) ? n : String(maybeId);
    },
    parseBool(v, fallback = false) {
      if (typeof v === 'boolean') return v;
      const s = String(v == null ? '' : v).toLowerCase();
      if (['1', 'true', 'yes', 'on'].includes(s)) return true;
      if (['0', 'false', 'no', 'off'].includes(s)) return false;
      return fallback;
    },
    parseDateToBoundary(text, startOfDay) {
      if (!text) return null;
      let dt = DateTime.fromFormat(text, this.datePathFormat);
      if (!dt.isValid) dt = DateTime.fromISO(text);
      if (!dt.isValid) dt = DateTime.fromFormat(text, 'yyyy-LL-dd HH:mm:ss');
      if (!dt.isValid) dt = DateTime.fromFormat(text, 'dd-LL-yyyy');
      if (!dt.isValid) return null;
      return startOfDay ? dt.startOf('day') : dt.endOf('day');
    },
    formatPicker(jsDate) {
      const dt = DateTime.fromJSDate(jsDate);
      return dt.isValid ? dt.toFormat('dd-LL-yyyy') : '';
    },
    parsePicker(text) {
      const dt = DateTime.fromFormat(String(text), 'dd-LL-yyyy');
      return dt.isValid ? dt.toJSDate() : new Date(NaN);
    },

    // Date shortcuts
    resetToToday() {
      const now = DateTime.local();
      this.state.startAt = now.startOf('day');
      this.state.endAt   = now.endOf('day');
    },
    resetToThisWeek() {
      const now = DateTime.local();
      this.state.startAt = now.startOf('week');
      this.state.endAt   = now.endOf('week');
    },
    resetToLastMonth() {
      const prev = DateTime.local().minus({ months: 1 });
      this.state.startAt = prev.startOf('month');
      this.state.endAt   = prev.endOf('month');
    },
    resetToThisMonth() {
      const now = DateTime.local();
      this.state.startAt = now.startOf('month');
      this.state.endAt   = now.endOf('month');
    },
    resetToLast6Months() {
      const now = DateTime.local();
      this.state.startAt = now.minus({ months: 6 }).startOf('day');
      this.state.endAt   = now.endOf('day');
    },
    resetToLastYear() {
      const now = DateTime.local();
      this.state.startAt = now.minus({ years: 1 }).startOf('day');
      this.state.endAt   = now.endOf('day');
    },
    resetToThisQuarter() {
      const now = DateTime.local();
      const quarter = Math.floor((now.month - 1) / 3) + 1;
      const startMonth = (quarter - 1) * 3 + 1;
      const start = DateTime.fromObject({ year: now.year, month: startMonth, day: 1 }).startOf('day');
      const end   = start.plus({ months: 3 }).minus({ days: 1 }).endOf('day');
      this.state.startAt = start;
      this.state.endAt   = end;
    },
    resetToLastQuarter() {
      const now = DateTime.local();
      const currentQuarter = Math.floor((now.month - 1) / 3) + 1;
      let quarter = currentQuarter - 1;
      let year = now.year;
      if (quarter === 0) { quarter = 4; year -= 1; }
      const startMonth = (quarter - 1) * 3 + 1;
      const start = DateTime.fromObject({ year, month: startMonth, day: 1 }).startOf('day');
      const end   = start.plus({ months: 3 }).minus({ days: 1 }).endOf('day');
      this.state.startAt = start;
      this.state.endAt   = end;
    },
  },
};
</script>

<style scoped lang="scss">
@use "../../scss/shared" as *;

.dr-button {
  width: 100%;
  height: 100%;
}

.buttons .button + .button { margin-left: .5rem; }
</style>
